// lib/models/invitation.ts

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled';

export interface Invitation {
    id: string;
    employer_id: string;
    organisation_id: string;
    email: string;
    status: InvitationStatus;
    message?: string;
    invited_at: string;
    accepted_at?: string;
    expires_at: string;
    created_at: string;
    // Display context (backend-enriched for the recipient): who/which org/unit.
    organisation_name?: string;
    unit_name?: string;
    invited_by_name?: string;
    invited_by_picture_url?: string;
    // Recipient (employer's sent-invitations view) — only set when the invited
    // email already belongs to a registered user.
    recipient_name?: string;
    recipient_picture_url?: string;
}

export interface InvitationListResponse {
    invitations: Invitation[];
    total: number;
}

export interface SendInvitationRequest {
    email: string;
    message?: string;
}

// ── Bulk invite (paste a list of emails, one request) ──────────────────────

export interface BatchSendInvitationRequest {
    emails: string[];
    message?: string;
}

export type BatchInviteStatus =
    | 'sent'
    | 'invalid'
    | 'already_member'
    | 'role_not_allowed'
    | 'failed';

export interface BatchInvitationResult {
    email: string;
    status: BatchInviteStatus;
    message?: string;       // human-readable reason (for non-sent outcomes)
    invitation?: Invitation; // present when status === 'sent'
}

export interface BatchSendInvitationResponse {
    results: BatchInvitationResult[];
    sent: number;
    skipped: number; // already_member + role_not_allowed
    invalid: number;
    failed: number;
}

export const BatchInviteStatusLabels: Record<BatchInviteStatus, string> = {
    sent: 'Inbjuden',
    invalid: 'Ogiltig adress',
    already_member: 'Redan medlem',
    role_not_allowed: 'Kan ej bjudas in',
    failed: 'Misslyckades',
};

// Public invitation info (returned by token lookup)
export interface InvitationInfo {
    id: string;
    email: string;
    status: string;
    /**
     * #355 — which relationship this invitation establishes, and therefore which
     * form the (public, pre-login) accept page renders:
     *   'employee' — an employer invited a co-worker. Accepting joins the org.
     *   'employer' — an admin created an employer account. "Accepting" means
     *                choosing a password for an account that has none yet.
     * Optional so an older backend that does not send it still parses; absent is
     * read as 'employee', matching the server-side default.
     */
    kind?: 'employee' | 'employer';
    organisation_id: string;
    /** Present so the pre-login page can name the workplace. May be empty. */
    organisation_name?: string;
    unit_name?: string;
    expires_at: string;
    is_expired: boolean;
}

export const InvitationStatusLabels: Record<InvitationStatus, string> = {
    pending: 'Väntande',
    accepted: 'Accepterad',
    expired: 'Utgången',
    cancelled: 'Avbruten',
};
